/**
 * Attorney Case Detail - Case Workspace with Intake Tab System
 *
 * Phase 4.2 - Intake Tab System (redesign-p4-intake-42b)
 *
 * Implements 4 sub-tabs for the Intake stage:
 * - Overview (#overview): Case Overview + Policy Coverage
 * - Parties: Insured, attorney, adjuster, other parties
 * - Document Checklist: Cross-document validation state
 * - Client Portal: Magic-link status + portal activity
 *
 * Design Spec: docs/plans/2026-06-25-redesign-design-spec.md
 * - Near-black surfaces (#0A0A0A base, #121212 cards)
 * - Orange (#FF6600) as only signal color
 * - Green (#22C55E) only for "done" state
 * - DM Serif Display / Outfit / DM Mono typography
 */

import React, { Suspense, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Scale,
  GitCompare,
  FileText,
  ScrollText,
  Compass,
  ShieldCheck,
  FileSearch,
  Download,
  ArrowLeft,
  Calendar,
  Loader2,
  RefreshCw,
  AlertCircle,
  Target,
  Shield,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  FileCheck,
  AlertTriangle,
  Info,
  Users,
  ClipboardList,
  UserCircle,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Mail,
  Link as LinkIcon,
  Clock3,
} from 'lucide-react';
import { getAuthHeaders } from '../lib/authHeaders';
import { api } from '../utils/api';
import { usePermissionsOptional } from '../contexts/PermissionsContext';
import AttorneyMethodologyDefenseView from './AttorneyMethodologyDefenseView';
import AttorneyLayout from '../components/AttorneyLayout';
import GuestInviteButton from '../components/attorney/GuestInviteButton';
import CaseTimeline, { generateTimelineEvents } from '../components/CaseTimeline';
import DraftWatermark from '../components/DraftWatermark';
import { DataTable, Column } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { StatusBadge } from '../components/shared/StatusBadge';
import { FinancialValue } from '../components/shared/FinancialValue';
import { MetricCard } from '../components/shared/MetricCard';
import { formatCurrency, NOT_COMPUTED_TEXT, ZERO_TEXT } from '../utils/financialFormat';
import { adjusterNotComputed } from '../services/notComputed';
import { BRAND_NAME } from '../config/brand';
import SendIntakeEmailModal from '../components/attorney/SendIntakeEmailModal';

// Lazy-loaded tool components
const SettlementIntelligenceDashboard = React.lazy(() => import('../components/analytics/SettlementIntelligenceDashboard'));
const GameTheoryDashboard = React.lazy(() => import('../components/analytics/GameTheoryDashboard'));
const BadFaithTracker = React.lazy(() => import('../components/analytics/BadFaithTracker'));
const CaseLawSearch = React.lazy(() => import('../components/analytics/CaseLawSearch'));
const EvidenceChecklist = React.lazy(() => import('../components/EvidenceChecklist'));
const WarRoomBriefPanel = React.lazy(() => import('../components/deliberation/BriefTab'));
const Texas542Card = React.lazy(() => import('../components/settlement/Texas542Card'));
const EngineAuditPanel = React.lazy(() => import('../components/EngineAuditPanel'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

interface PolicyCoverage {
  claim_type: 'first_party' | 'third_party' | 'unknown';
  responsible_party?: string;
  bi_limit?: number;
  extra_expense_limit?: number;
  combined_limit?: number;
  deductible?: number;
  waiting_period_days?: number;
  exclusions?: string[];
  policy_document_url?: string;
  policy_number?: string;
  insurer_name?: string;
  effective_date?: string;
  expiration_date?: string;
}

interface Party {
  id: string;
  role: 'insured' | 'attorney' | 'adjuster' | 'accountant' | 'other';
  name: string;
  email?: string;
  phone?: string;
  organization?: string;
  is_primary: boolean;
}

interface DocumentChecklistItem {
  id: string;
  document_type: string;
  label: string;
  status: 'present' | 'missing' | 'pending' | 'not_required';
  uploaded_at?: string;
  file_name?: string;
}

interface ClientPortalStatus {
  is_initialized: boolean;
  magic_link_url?: string;
  magic_link_expires_at?: string;
  last_accessed_at?: string;
  access_count: number;
  uploads_count: number;
  clarifications_count: number;
}

interface BindingFigures {
  net_supported_claim: number | null;
  reconciled_sworn_figure: number | null;
  settlement_p50: number | null;
  run_id: string | null;
  computed_at: string | null;
}

interface CaseDetail {
  id: string;
  client_name: string;
  business_name?: string;
  event_type?: string;
  event_date?: string;
  state?: string;
  total_demand?: number;
  released_at: string;
  calculation_mode?: string;
  attorney_analysis?: any;
  adjuster_analysis?: any;
  fair_settlement?: any;
  pro_forma_summary?: any;
  monte_carlo_results?: any;
  policy_coverage?: PolicyCoverage;
  parties?: Party[];
  document_checklist?: DocumentChecklistItem[];
  client_portal?: ClientPortalStatus;
  // Audit/lifecycle metadata the backend also returns (created/submitted/released
  // by-name + timestamps, download history) was typed here but never read — dropped
  // to keep this interface to what the page actually consumes. Re-add the specific
  // field if/when a surface wires it.
  status?: string;
  binding_figures?: BindingFigures | null;
}

type IntakeTab = 'overview' | 'parties' | 'documents' | 'client-portal';

// -------------------------------------------------------------------------
// Legacy Hash Anchor Mapping (Phase 4.8 - Hash Deep-links)
// -------------------------------------------------------------------------
// Maps legacy URL hashes to their new tab + anchor locations in the redesigned
// AttorneyCaseDetail lifecycle tab structure.
// Source: docs/plans/2026-06-26-redesign-surface-atlas.md §1, §5 #2

interface LegacyHashTarget {
  /** The lifecycle stage tab to activate */
  tab: 'intake' | 'settlement' | 'analysis' | 'deliverables';
  /** The in-tab anchor element id to scroll to */
  anchor: string;
  /** For settlement sub-tabs that need state selection */
  settlementSubTab?: 'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation';
}

export const LEGACY_HASH_MAP: Record<string, LegacyHashTarget> = {
  // Intake stage
  'overview': { tab: 'intake', anchor: 'overview' },

  // Analysis stage (rendered inline in current AttorneyCaseDetail)
  'attorney-analysis': { tab: 'analysis', anchor: 'attorney-analysis' },
  'adjuster-analysis': { tab: 'analysis', anchor: 'adjuster-analysis' },
  'monte-carlo': { tab: 'analysis', anchor: 'monte-carlo' },

  // Deliverables stage (pro-forma moved here 2026-08-20 — see the Pro Forma card)
  'pro-forma': { tab: 'deliverables', anchor: 'pro-forma' },

  // Settlement stage
  'fair-settlement': { tab: 'settlement', anchor: 'fair-settlement' },
  'sensitivity': { tab: 'settlement', anchor: 'sensitivity', settlementSubTab: 'sensitivity' },
  'historical': { tab: 'settlement', anchor: 'historical', settlementSubTab: 'historical' },
  'what-if': { tab: 'settlement', anchor: 'what-if', settlementSubTab: 'whatif' },
  'strategy-builder': { tab: 'settlement', anchor: 'strategy-builder', settlementSubTab: 'builder' },
  'litigation-econ': { tab: 'settlement', anchor: 'litigation-econ', settlementSubTab: 'litigation' },
  'game-theory': { tab: 'settlement', anchor: 'game-theory' },
  'bad-faith': { tab: 'settlement', anchor: 'bad-faith' },
  'case-law': { tab: 'settlement', anchor: 'case-law' },
  'evidence': { tab: 'settlement', anchor: 'evidence' },
  'timeline': { tab: 'settlement', anchor: 'timeline' },

  // Deliverables stage
  'demand-letter': { tab: 'deliverables', anchor: 'demand-letter' },
  'proof-of-loss': { tab: 'deliverables', anchor: 'proof-of-loss' },
  'euo-prep-memo': { tab: 'deliverables', anchor: 'euo-prep-memo' },
  'ror-response': { tab: 'deliverables', anchor: 'ror-response' },
  'ror-analysis': { tab: 'deliverables', anchor: 'ror-analysis' },
};

// -------------------------------------------------------------------------
// Main Component
// -------------------------------------------------------------------------

export default function AttorneyCaseDetail() {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<CaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState<'pdf' | 'docx' | null>(null);
  const [rerunning, setRerunning] = useState<'engines' | 'analysis' | null>(null);
  const perms = usePermissionsOptional();
  // Re-running is a privileged, cost-bearing op (recomputes engines + re-runs the
  // attorney AI pass). The backend enforces this too (fleet-auth / admin role); the
  // gate here just hides the control from non-privileged viewers.
  const canRerun = ['admin', 'owner'].includes(perms?.role || '');
  const [activeSection, setActiveSection] = useState<string>('');

  // Intake tab state
  const [activeIntakeTab, setActiveIntakeTab] = useState<IntakeTab>('overview');
  const [portalLoading, setPortalLoading] = useState(false);
  const [showSendIntakeEmailModal, setShowSendIntakeEmailModal] = useState(false);

  // Lifecycle stage tab state (Phase 4.8 - hash deep-link support)
  type LifecycleStage = 'intake' | 'analysis' | 'methodology' | 'deliverables' | 'settlement';
  const [activeLifecycleTab, setActiveLifecycleTab] = useState<LifecycleStage>('intake');

  // Section refs for scrolling
  const policyCoverageRef = useRef<HTMLDivElement>(null);
  const attorneyAnalysisRef = useRef<HTMLDivElement>(null);
  const adjusterAnalysisRef = useRef<HTMLDivElement>(null);
  const fairSettlementRef = useRef<HTMLDivElement>(null);
  const monteCarloRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Section collapse states
  const [showPolicyCoverage, setShowPolicyCoverage] = useState(false);
  const [showAttorneyAnalysis, setShowAttorneyAnalysis] = useState(true);
  const [showAdjusterAnalysis, setShowAdjusterAnalysis] = useState(true);
  const [showFairSettlement, setShowFairSettlement] = useState(true);
  const [showProForma, setShowProForma] = useState(true);
  const [showTimeline, setShowTimeline] = useState(true);

  // Policy coverage state
  const [claimType, setClaimType] = useState<'first_party' | 'third_party' | 'unknown'>('unknown');
  const [updatingClaimType, setUpdatingClaimType] = useState(false);

  // Settlement dashboard tab state
  const [settlementTab, setSettlementTab] = useState<'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation'>('sensitivity');

  const navigate = useNavigate();
  const location = useLocation();

  const settlementTabMap: Record<string, 'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation'> = {
    'sensitivity': 'sensitivity',
    'historical': 'historical',
    'what-if': 'whatif',
    'strategy-builder': 'builder',
    'litigation-econ': 'litigation',
  };

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  useEffect(() => {
    // Auth check (legacy JWT or Clerk) happens inside fetchCaseDetail itself
    // via getAuthHeaders()/the 401 handler — a synchronous pre-check here
    // can't see a Clerk session (getAuthToken() is async), so it would
    // wrongly bounce a Clerk-authed attorney straight to /attorney/login.
    fetchCaseDetail();
  }, [caseId]);

  // Handle URL hash for #overview deep-link
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.replace('#', '');
      if (hash === 'overview') {
        setActiveIntakeTab('overview');
      }
    }
  }, [location.hash]);

  // -------------------------------------------------------------------------
  // Phase 4.8 - Legacy Hash Anchor Redirect Effect
  // Maps legacy URL hashes to new lifecycle tabs and scrolls to anchor
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!caseData) return;

    const hash = location.hash.replace('#', '');
    if (!hash) return;

    const target = LEGACY_HASH_MAP[hash];
    if (!target) return;

    // Activate the appropriate lifecycle tab
    setActiveLifecycleTab(target.tab);

    // For settlement sub-tabs, set the appropriate tab state
    if (target.settlementSubTab) {
      setSettlementTab(target.settlementSubTab);
    }

    // For intake tab, also set the intake sub-tab if needed
    if (target.tab === 'intake' && hash === 'overview') {
      setActiveIntakeTab('overview');
    }

    // Scroll to the anchor after tab switch renders
    setTimeout(() => {
      const element = document.getElementById(target.anchor);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 100);
  }, [location.hash, caseData]);

  // -------------------------------------------------------------------------
  // Data Fetching
  // -------------------------------------------------------------------------

  const fetchCaseDetail = async () => {
    setLoading(true);
    setError('');

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}`, {
        headers: authHeaders
      });

      if (response.status === 401) {
        navigate('/attorney/login');
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to fetch case');
      }

      const data = await response.json();
      setCaseData(data);
      if (data.policy_coverage?.claim_type) {
        setClaimType(data.policy_coverage.claim_type);
      }

      // Fetch client portal status if not included
      if (!data.client_portal) {
        fetchClientPortalStatus();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientPortalStatus = async () => {
    setPortalLoading(true);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/portal-status`, {
        headers: authHeaders
      });

      if (response.ok) {
        const portalData = await response.json();
        setCaseData(prev => prev ? { ...prev, client_portal: portalData } : null);
      }
    } catch (err) {
      console.error('Failed to fetch portal status:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Event Handlers
  // -------------------------------------------------------------------------

  const handleSectionChange = (section: string) => {
    setActiveSection(section);

    if (section === 'download-pdf') {
      handleDownload('pdf');
      return;
    }
    if (section === 'download-docx') {
      handleDownload('docx');
      return;
    }

    if (settlementTabMap[section]) {
      setSettlementTab(settlementTabMap[section]);
      const el = document.getElementById('sensitivity');
      if (el) {
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
      return;
    }

    let ref: React.RefObject<HTMLDivElement> | null = null;

    switch (section) {
      case 'policy-coverage':
        ref = policyCoverageRef;
        setShowPolicyCoverage(true);
        break;
      case 'attorney-analysis':
        ref = attorneyAnalysisRef;
        setShowAttorneyAnalysis(true);
        break;
      case 'adjuster-analysis':
        ref = adjusterAnalysisRef;
        setShowAdjusterAnalysis(true);
        break;
      case 'fair-settlement':
        ref = fairSettlementRef;
        setShowFairSettlement(true);
        break;
      case 'monte-carlo':
        ref = monteCarloRef;
        break;
      case 'timeline':
        ref = timelineRef;
        setShowTimeline(true);
        break;
    }

    if (ref?.current) {
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    } else {
      const el = document.getElementById(section);
      if (el) {
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }
  };

  // Re-run the deterministic engine suite (refreshes the Monte Carlo / settlement
  // figures) then the attorney AI pass (refreshes the summary), then reload the
  // case. Replaces the manual DevTools-console dance. Both calls are admin/owner
  // gated server-side; api.post attaches the Clerk bearer automatically.
  const handleRerun = async () => {
    if (!caseId || rerunning) return;
    try {
      setRerunning('engines');
      toast.info('Re-running engines… (~1–3 min)');
      await api.post(`/api/v1/fleet/${caseId}/engine-rerun`, {});
      setRerunning('analysis');
      toast.info('Refreshing analysis…');
      // Best-effort: the summary refresh is secondary to the engine figures.
      try {
        await api.post(`/api/v1/orchestrator/admin/trigger-analysis/${caseId}`, {});
      } catch (e) {
        console.warn('trigger-analysis (summary refresh) failed; engine figures still updated', e);
      }
      await fetchCaseDetail();
      toast.success('Analysis re-run complete — figures refreshed.');
    } catch (e: any) {
      toast.error(e?.message || 'Re-run failed. Please try again.');
    } finally {
      setRerunning(null);
    }
  };

  const handleDownload = async (format: 'pdf' | 'docx') => {
    setDownloading(format);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(
        `${BACKEND_URL}/api/v1/attorney/case/${caseId}/download/${format}`,
        { headers: authHeaders }
      );

      if (!response.ok) throw new Error('Download failed');

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Report.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(`Download failed: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const handleClaimTypeChange = async (newType: 'first_party' | 'third_party') => {
    setUpdatingClaimType(true);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/claim-type`, {
        method: 'PATCH',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ claim_type: newType })
      });

      if (response.ok) {
        setClaimType(newType);
        if (caseData) {
          setCaseData({
            ...caseData,
            policy_coverage: { ...caseData.policy_coverage, claim_type: newType }
          });
        }
      }
    } catch (err) {
      console.error('Failed to update claim type:', err);
    } finally {
      setUpdatingClaimType(false);
    }
  };

  const handleDownloadPolicy = async () => {
    if (!caseData?.policy_coverage?.policy_document_url) return;
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/policy-document`, {
        headers: authHeaders
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Policy_${caseData.client_name || 'document'}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to download policy:', err);
    }
  };

  const getPolicyWarning = () => {
    if (!caseData?.policy_coverage || claimType !== 'first_party') return null;
    const policy = caseData.policy_coverage;
    const limit = policy.combined_limit || policy.bi_limit || 0;

    // Primary comparison uses binding_figures.net_supported_claim when available
    const primaryValue = caseData.binding_figures?.net_supported_claim;
    const fallbackDemand = caseData.total_demand || 0;

    if (limit > 0 && primaryValue != null && primaryValue > limit) {
      const excess = primaryValue - limit;
      const demandAlsoExceeds = fallbackDemand > limit;
      return {
        type: 'warning' as const,
        message: `Supported claim exceeds policy limit by ${formatCurrency(excess)}`,
        secondaryNote: demandAlsoExceeds ? 'Total demand also exceeds limit' : undefined
      };
    }

    // Fallback to total_demand when net_supported_claim is null
    if (limit > 0 && primaryValue == null && fallbackDemand > limit) {
      const excess = fallbackDemand - limit;
      return {
        type: 'warning' as const,
        message: `Calculated loss exceeds policy limit by ${formatCurrency(excess)}`
      };
    }

    return null;
  };

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  /**
   * True only when the pro forma actually carries a figure. The endpoint
   * returns `{}` when the future-closure engine has not run for this case,
   * and `{}` is truthy — which both rendered the summary card empty AND
   * suppressed the "Analysis not available yet" empty state.
   */
  const hasProFormaFigures = Object.values(caseData?.pro_forma_summary || {}).some(
    (v) => v !== null && v !== undefined && v !== '' && v !== 0
  );

  // Fair-Settlement card renders only when the server sent a (quarantined) figure
  // block. The composed P50/adjuster "recommendation" is gone (owner ruling
  // 2026-09-10); the section now leads with the engine-attested supported net and
  // demotes the Monte-Carlo band to a labeled statistical cross-check.
  const hasFairSettlementFigures = !!(
    caseData?.fair_settlement &&
    typeof caseData.fair_settlement !== 'string' &&
    caseData.fair_settlement.cross_check
  );

  /** Backend vocabulary is snake_case ("fire", "in_progress"); attorneys read prose. */
  const titleCase = (raw: string) =>
    raw.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  /**
   * Parse a backend date. A date-only string ("2023-03-15") is parsed by
   * `new Date()` as UTC midnight, which renders as the PREVIOUS day for any
   * viewer west of Greenwich — the loss date on this case read "March 14"
   * for a 2023-03-15 event. Split date-only strings and build a LOCAL date.
   */
  const parseDate = (dateStr: string): Date => {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (dateOnly) {
      return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    }
    return new Date(dateStr);
  };

  const formatDate = (dateStr?: string, style: 'long' | 'short' = 'long') => {
    if (!dateStr) return 'N/A';
    const d = parseDate(dateStr);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: style === 'short' ? 'short' : 'long', day: 'numeric'
    });
  };

  // -------------------------------------------------------------------------
  // Intake Tab Content Components
  // -------------------------------------------------------------------------

  const renderOverviewTab = () => {
    const policy = caseData?.policy_coverage;
    const hasPolicyLimits = !!(policy?.bi_limit || policy?.combined_limit);
    const hasPolicyMeta = !!(policy?.insurer_name || policy?.policy_number || policy?.effective_date || policy?.expiration_date);
    const hasNoPolicyData = !hasPolicyLimits && !policy?.policy_number && !policy?.insurer_name;

    return (
    <div className="space-y-6" data-testid="intake-panel-overview">
      {/* Case Overview Card */}
      <div
        className="rounded-[13px] border p-6"
        style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <h3
          className="cs-h2 mb-4"
        >
          Case Overview
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="Planned Demand"
            value={<FinancialValue value={caseData?.total_demand} size="lg" />}
            accent={true}
          />
          <MetricCard
            label="Event Type"
            value={caseData?.event_type ? titleCase(caseData.event_type) : '—'}
          />
          <MetricCard
            label="Event Date"
            value={caseData?.event_date ? formatDate(caseData.event_date, 'short') : '—'}
          />
          {/* One status tile, not a value plus a badge restating it. The value
              is the real lifecycle status ('—' when the backend sent none),
              never an invented default. */}
          <MetricCard
            label="Status"
            value={caseData?.status ? titleCase(caseData.status.replace(/_/g, ' ')) : '—'}
            subtitle={
              !caseData?.status
                ? 'Status unavailable'
                : caseData.status === 'final'
                  ? 'Case finalized'
                  : 'Work in progress'
            }
          />
        </div>
      </div>

      {/* Policy Coverage Section */}
      <div
        id="overview"
        ref={policyCoverageRef}
        className="rounded-[13px] border overflow-hidden"
        style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
        data-testid="policy-coverage-section"
      >
        <button
          onClick={() => setShowPolicyCoverage(!showPolicyCoverage)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--color-bg-elevated)] transition-colors"
          data-testid="policy-coverage-toggle"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
            >
              <FileCheck className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            </div>
            <div className="text-left">
              <h2
                className="cs-h2"
              >
                Policy Coverage
              </h2>
              <p style={{ color: 'var(--color-text-muted)' }}>
                {claimType === 'first_party' ? 'First-party claim' :
                 claimType === 'third_party' ? 'Third-party claim' : 'Claim type not set'}
              </p>
            </div>
          </div>
          {showPolicyCoverage ? (
            <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
          ) : (
            <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
          )}
        </button>

        {showPolicyCoverage && (
          <div className="px-6 pb-6 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
            <div className="pt-4 space-y-4">
              {/* Claim Type Selection */}
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Claim Type:</span>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="claim_type"
                      checked={claimType === 'first_party'}
                      onChange={() => handleClaimTypeChange('first_party')}
                      disabled={updatingClaimType}
                      className="w-4 h-4"
                      style={{ accentColor: 'var(--color-brand)' }}
                      data-testid="claim-type-first-party"
                    />
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>First-Party</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="claim_type"
                      checked={claimType === 'third_party'}
                      onChange={() => handleClaimTypeChange('third_party')}
                      disabled={updatingClaimType}
                      className="w-4 h-4"
                      style={{ accentColor: 'var(--color-brand)' }}
                      data-testid="claim-type-third-party"
                    />
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Third-Party</span>
                  </label>
                  {updatingClaimType && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--color-text-dim)' }} />}
                </div>
              </div>

              {/* First-Party Content */}
              {claimType === 'first_party' && (
                <>
                  {/* Policy Metadata (insurer / policy number / period) */}
                  {hasPolicyMeta && (
                    <div
                      className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-lg"
                      style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                      data-testid="policy-metadata-row"
                    >
                      {policy?.insurer_name && (
                        <div>
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Insurer</p>
                          <p style={{ color: 'var(--color-text-secondary)' }}>{policy.insurer_name}</p>
                        </div>
                      )}
                      {policy?.policy_number && (
                        <div>
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Policy Number</p>
                          <p className="font-mono" style={{ color: 'var(--color-text-primary)' }}>{policy.policy_number}</p>
                        </div>
                      )}
                      {(policy?.effective_date || policy?.expiration_date) && (
                        <div>
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Policy Period</p>
                          <p style={{ color: 'var(--color-text-secondary)' }}>
                            {policy?.effective_date ? formatDate(policy.effective_date) : '—'}
                            {' – '}
                            {policy?.expiration_date ? formatDate(policy.expiration_date) : '—'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {hasPolicyLimits ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {caseData.policy_coverage.bi_limit && (
                          <div
                            className="rounded-lg p-4"
                            style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                          >
                            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>BI Limit</p>
                            <FinancialValue value={caseData.policy_coverage.bi_limit} size="lg" />
                          </div>
                        )}
                        {caseData.policy_coverage.extra_expense_limit && (
                          <div
                            className="rounded-lg p-4"
                            style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                          >
                            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Extra Expense</p>
                            <FinancialValue value={caseData.policy_coverage.extra_expense_limit} size="lg" />
                          </div>
                        )}
                        {caseData.policy_coverage.deductible && (
                          <div
                            className="rounded-lg p-4"
                            style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                          >
                            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Deductible</p>
                            <FinancialValue value={caseData.policy_coverage.deductible} size="lg" />
                          </div>
                        )}
                        {caseData.policy_coverage.waiting_period_days && (
                          <div
                            className="rounded-lg p-4"
                            style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                          >
                            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Waiting Period</p>
                            <p
                              className="text-lg font-bold font-mono"
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              {caseData.policy_coverage.waiting_period_days} days
                            </p>
                          </div>
                        )}
                      </div>

                      {getPolicyWarning() && (
                        <div
                          className="flex items-start gap-3 p-4 rounded-lg"
                          style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid rgba(255,102,0,0.30)' }}
                        >
                          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
                          <div>
                            <p className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Coverage Limit Warning</p>
                            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{getPolicyWarning()?.message}</p>
                            {getPolicyWarning()?.secondaryNote && (
                              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                                {getPolicyWarning()?.secondaryNote}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {caseData.policy_coverage.exclusions && caseData.policy_coverage.exclusions.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Key Exclusions</p>
                          <div className="flex flex-wrap gap-2">
                            {caseData.policy_coverage.exclusions.map((exclusion, i) => (
                              <span
                                key={i}
                                className="px-3 py-1 rounded-full text-sm"
                                style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
                              >
                                {exclusion}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {caseData.policy_coverage.policy_document_url && (
                        <div className="flex items-center gap-3 pt-2">
                          <button
                            onClick={handleDownloadPolicy}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                            style={{
                              backgroundColor: 'var(--color-needs-human-tint)',
                              color: 'var(--color-needs-human-text)'
                            }}
                            data-testid="download-policy-btn"
                          >
                            <Download className="w-4 h-4" />
                            View Full Policy
                          </button>
                        </div>
                      )}
                    </>
                  ) : hasNoPolicyData ? (
                    <EmptyState
                      icon={<FileCheck className="w-8 h-8" style={{ color: 'var(--color-text-dim)' }} />}
                      title="No policy on file"
                      description="Policy limits not available. Upload the policy document to enable coverage analysis."
                    />
                  ) : null}
                </>
              )}

              {/* Third-Party Content */}
              {claimType === 'third_party' && (
                <div
                  className="p-4 rounded-lg border"
                  style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
                >
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-muted)' }} />
                    <div>
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Third-party claim</p>
                      <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                        This is a claim against the responsible party's insurance.
                        Client's policy limits do not cap recoverable damages.
                      </p>
                      {caseData?.policy_coverage?.responsible_party && (
                        <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                          <span className="font-medium">Responsible Party:</span> {caseData.policy_coverage.responsible_party}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Unknown Claim Type */}
              {claimType === 'unknown' && (
                <div
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                >
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Please select a claim type above to view coverage information.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderPartiesTab = () => {
    const parties = caseData?.parties || [];
    const partyColumns: Column<Party>[] = [
      {
        key: 'role',
        label: 'Role',
        render: (value: string) => (
          <span className="capitalize" style={{ color: 'var(--color-text-secondary)' }}>{value.replace(/_/g, ' ')}</span>
        )
      },
      { key: 'name', label: 'Name' },
      { key: 'organization', label: 'Organization' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      {
        key: 'is_primary',
        label: 'Primary',
        render: (value: boolean) => value ? (
          <StatusBadge text="Primary" variant="active" />
        ) : (
          <span style={{ color: 'var(--color-text-dim)' }}>—</span>
        )
      }
    ];

    return (
      <div data-testid="intake-panel-parties">
        {parties.length > 0 ? (
          <DataTable
            columns={partyColumns}
            data={parties}
            emptyMessage="No parties found"
          />
        ) : (
          <div
            className="rounded-[13px] border p-8"
            style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
          >
            <EmptyState
              icon={<Users className="w-12 h-12" style={{ color: 'var(--color-text-dim)' }} />}
              title="No parties on file"
              description="Party information will appear here once the case is fully initialized."
            />
          </div>
        )}
      </div>
    );
  };

  const renderDocumentChecklistTab = () => {
    const documents = caseData?.document_checklist || [];

    const getStatusBadge = (status: string) => {
      switch (status) {
        case 'present':
          return <StatusBadge text="Received" variant="complete" />;
        case 'missing':
          return <StatusBadge text="Missing" variant="error" />;
        case 'pending':
          return <StatusBadge text="Pending" variant="pending" />;
        case 'not_required':
          return <StatusBadge text="N/A" variant="neutral" />;
        default:
          return <StatusBadge text={status} variant="neutral" />;
      }
    };

    const getStatusIcon = (status: string) => {
      switch (status) {
        case 'present':
          return <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />;
        case 'missing':
          return <XCircle className="w-5 h-5" style={{ color: 'var(--color-blocker)' }} />;
        case 'pending':
          return <Clock3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />;
        default:
          return <MinusCircle className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />;
      }
    };

    return (
      <div data-testid="intake-panel-documents">
        {documents.length > 0 ? (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-4 p-4 rounded-lg border"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                {getStatusIcon(doc.status)}
                <div className="flex-1">
                  <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{doc.label}</p>
                  {doc.file_name && (
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{doc.file_name}</p>
                  )}
                </div>
                {getStatusBadge(doc.status)}
              </div>
            ))}
          </div>
        ) : (
          <div
            className="rounded-[13px] border p-8"
            style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
          >
            <EmptyState
              icon={<ClipboardList className="w-12 h-12" style={{ color: 'var(--color-text-dim)' }} />}
              title="No document checklist available"
              description="Document checklist will be generated once documents are uploaded and classified."
            />
          </div>
        )}
      </div>
    );
  };

  const renderClientPortalTab = () => {
    const portal = caseData?.client_portal;

    return (
      <div data-testid="intake-panel-client-portal">
        {portalLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
        ) : portal?.is_initialized ? (
          <div className="space-y-6">
            {/* Portal Status Card */}
            <div
              className="rounded-[13px] border p-6"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center justify-between mb-6">
                <h3
                  className="cs-h2"
                >
                  Portal Status
                </h3>
                <StatusBadge
                  text={portal.magic_link_expires_at && new Date(portal.magic_link_expires_at) > new Date()
                    ? "Active"
                    : "Expired"
                  }
                  variant={portal.magic_link_expires_at && new Date(portal.magic_link_expires_at) > new Date()
                    ? "active"
                    : "error"
                  }
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  label="Access Count"
                  value={portal.access_count?.toString() || '0'}
                />
                <MetricCard
                  label="Uploads"
                  value={portal.uploads_count?.toString() || '0'}
                />
                <MetricCard
                  label="Clarifications"
                  value={portal.clarifications_count?.toString() || '0'}
                />
                <MetricCard
                  label="Last Accessed"
                  value={portal.last_accessed_at
                    ? new Date(portal.last_accessed_at).toLocaleDateString()
                    : 'Never'
                  }
                />
              </div>
            </div>

            {/* Magic Link Card */}
            {portal.magic_link_url && (
              <div
                className="rounded-[13px] border p-6"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <h3
                  className="cs-h2 mb-4"
                >
                  Magic Link
                </h3>
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <LinkIcon className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                  <code
                    className="flex-1 text-sm truncate font-mono"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {portal.magic_link_url}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(portal.magic_link_url!);
                      toast.success('Link copied to clipboard');
                    }}
                    className="px-3 py-1.5 rounded text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: 'var(--color-needs-human-tint)',
                      color: 'var(--color-needs-human-text)'
                    }}
                  >
                    Copy
                  </button>
                </div>
                {portal.magic_link_expires_at && (
                  <p className="mt-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Expires: {new Date(portal.magic_link_expires_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            className="rounded-[13px] border p-8"
            style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
          >
            <EmptyState
              icon={<UserCircle className="w-12 h-12" style={{ color: 'var(--color-text-dim)' }} />}
              title="Client Portal Not Initialized"
              description="The client portal is set up automatically once the client engages with an intake email. Send it now to invite them to upload documents and categorize transactions."
              action={
                <button
                  onClick={() => setShowSendIntakeEmailModal(true)}
                  className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                  style={{
                    backgroundColor: 'var(--color-brand)',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  <Mail className="w-4 h-4" />
                  Send Client Intake Email
                </button>
              }
            />
          </div>
        )}

        <SendIntakeEmailModal
          isOpen={showSendIntakeEmailModal}
          onClose={() => setShowSendIntakeEmailModal(false)}
          caseId={caseId || ''}
          clientName={caseData?.client_name}
          defaultEmail={caseData?.parties?.find((p) => p.role === 'insured' && p.email)?.email}
        />
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main Render
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <AttorneyLayout activeSection={activeSection} onSectionChange={handleSectionChange}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: 'var(--color-needs-human-text)' }} />
            <p style={{ color: 'var(--color-text-muted)' }}>Loading case details...</p>
          </div>
        </div>
      </AttorneyLayout>
    );
  }

  if (error || !caseData) {
    return (
      <AttorneyLayout activeSection={activeSection} onSectionChange={handleSectionChange}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-needs-human-text)' }} />
            <h2
              className="cs-h2 mb-2"
            >
              Error Loading Case
            </h2>
            <p className="mb-6" style={{ color: 'var(--color-text-muted)' }}>{error || 'Case not found'}</p>
            <Link
              to="/attorney/dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </AttorneyLayout>
    );
  }

  return (
    <AttorneyLayout activeSection={activeSection} onSectionChange={handleSectionChange}>
      <div className="relative">
        <DraftWatermark status={caseData?.status} />

        {/* Back button and Download Buttons */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/attorney/dashboard"
            className="flex items-center gap-2 transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-muted)'}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </Link>

          <div className="flex items-center gap-2">
            <GuestInviteButton caseId={caseId || ''} />
            {canRerun && (
              <button
                onClick={handleRerun}
                disabled={rerunning !== null}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
                style={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)'
                }}
                data-testid="rerun-analysis-button"
                title="Re-run the engine suite and analysis for this case"
              >
                {rerunning ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {rerunning === 'engines' ? 'Re-running engines…'
                  : rerunning === 'analysis' ? 'Refreshing…'
                  : 'Re-run Analysis'}
              </button>
            )}
            <button
              onClick={() => handleDownload('pdf')}
              disabled={downloading !== null}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              style={{
                backgroundColor: 'var(--color-needs-human-tint)',
                color: 'var(--color-needs-human-text)'
              }}
              data-testid="download-pdf-button"
            >
              {downloading === 'pdf' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              PDF
            </button>
            <button
              onClick={() => handleDownload('docx')}
              disabled={downloading !== null}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              style={{
                backgroundColor: 'var(--color-bg-elevated)',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)'
              }}
              data-testid="download-docx-button"
            >
              {downloading === 'docx' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Word
            </button>
          </div>
        </div>

        {/* Case Header */}
        <div
          className="rounded-[13px] border p-6 mb-6"
          style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1
                className="cs-display"
              >
                {caseData.client_name}
              </h1>
              {/* Only a DISTINCT business name adds information — on most cases
                  the insured and the business are the same string, and echoing
                  it back reads as a rendering bug. */}
              {caseData.business_name && caseData.business_name !== caseData.client_name && (
                <p style={{ color: 'var(--color-text-muted)' }}>{caseData.business_name}</p>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {caseData.event_type && (
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
                  >
                    {titleCase(caseData.event_type)}
                  </span>
                )}
                {caseData.calculation_mode && (
                  <span
                    className="px-3 py-1 rounded-full text-sm"
                    style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
                  >
                    {caseData.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'}
                  </span>
                )}
                {/* An unreleased case has no release date — "Released N/A" is
                    noise, not a status. Show the chip only once it is real. */}
                {caseData.released_at && (
                  <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--color-text-dim)' }}>
                    <Calendar className="w-4 h-4" />
                    Released {formatDate(caseData.released_at)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              {/* Planned Demand KPI — the RECOMMENDED opening ask (analytical),
                  NOT a record of an issued demand. Labeling a computed figure
                  "Demanded" would assert a filed demand that may never have
                  happened (a false record on an exhibit). Kept distinct from an
                  issued-demand record, which the platform does not track today. */}
              <div
                className="rounded-xl p-4 text-center md:text-right"
                style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
              >
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Planned Demand</p>
                <FinancialValue value={caseData.total_demand} size="xl" className="text-3xl font-bold" />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
                  recommended opening · not yet issued
                </p>
              </div>

              {/* Supported (net claim) KPI */}
              <div
                className="rounded-xl p-4 text-center md:text-right"
                style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
              >
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Supported (net claim)</p>
                <FinancialValue value={caseData.binding_figures?.net_supported_claim} size="xl" className="text-3xl font-bold" />
                {caseData.binding_figures?.computed_at && (
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
                    engine run {new Date(caseData.binding_figures.computed_at).toLocaleDateString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Lifecycle Stage Tabs (Phase 4.8) */}
        <div
          className="flex items-center gap-1 mb-6 p-1 rounded-lg"
          style={{ backgroundColor: 'var(--color-bg-elevated)' }}
          data-testid="lifecycle-tab-bar"
        >
          {[
            { id: 'intake', label: 'Intake', icon: FileCheck },
            { id: 'analysis', label: 'Analysis', icon: Scale },
            { id: 'methodology', label: 'Methodology', icon: GitCompare },
            { id: 'deliverables', label: 'Deliverables', icon: FileText },
            { id: 'settlement', label: 'Settlement', icon: DollarSign },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeLifecycleTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveLifecycleTab(tab.id as LifecycleStage)}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all font-sans"
                style={{
                  backgroundColor: isActive ? 'var(--color-needs-human-tint)' : 'transparent',
                  color: isActive ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)',
                  borderBottom: isActive ? '2px solid var(--color-brand)' : '2px solid transparent',
                }}
                data-testid={`lifecycle-tab-${tab.id}`}
                aria-selected={isActive}
                role="tab"
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Intake Stage Content */}
        {activeLifecycleTab === 'intake' && (
          <>
            {/* Intake Sub-Tab Bar */}
            <div
              className="flex items-center gap-1 mb-6 p-1 rounded-lg"
              style={{ backgroundColor: 'var(--color-bg-elevated)' }}
              data-testid="intake-tab-bar"
            >
              {[
                { id: 'overview', label: 'Overview', icon: FileCheck },
                { id: 'parties', label: 'Parties', icon: Users },
                { id: 'documents', label: 'Document Checklist', icon: ClipboardList },
                { id: 'client-portal', label: 'Client Portal', icon: UserCircle },
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = activeIntakeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveIntakeTab(tab.id as IntakeTab)}
                    className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all font-sans"
                    style={{
                      backgroundColor: isActive ? 'var(--color-needs-human-tint)' : 'transparent',
                      color: isActive ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)',
                      borderBottom: isActive ? '2px solid var(--color-brand)' : '2px solid transparent',
                    }}
                    data-testid={`intake-tab-${tab.id}`}
                    aria-selected={isActive}
                    role="tab"
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Intake Tab Content */}
            <div role="tabpanel">
              {activeIntakeTab === 'overview' && renderOverviewTab()}
              {activeIntakeTab === 'parties' && renderPartiesTab()}
              {activeIntakeTab === 'documents' && renderDocumentChecklistTab()}
              {activeIntakeTab === 'client-portal' && renderClientPortalTab()}
            </div>
          </>
        )}

        {/* Analysis Stage Content */}
        {activeLifecycleTab === 'analysis' && (
          <>
            {!caseData.attorney_analysis && !caseData.adjuster_analysis && !caseData.monte_carlo_results && (
              <div
                className="rounded-[13px] border p-8"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <EmptyState
                  icon={<Scale className="w-12 h-12" style={{ color: 'var(--color-text-dim)' }} />}
                  title="Analysis not available yet"
                  description="Attorney analysis, adjuster analysis, and Monte Carlo results will appear here once the case has been analyzed."
                />
              </div>
            )}

            {/* Attorney Analysis Section */}
            {caseData.attorney_analysis && (
              <div
                id="attorney-analysis"
                ref={attorneyAnalysisRef}
                className="rounded-[13px] border mb-6 overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <button
                  onClick={() => setShowAttorneyAnalysis(!showAttorneyAnalysis)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                    >
                      <Scale className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    </div>
                    <div className="text-left">
                      <h2
                        className="cs-h2"
                      >
                        Attorney Analysis
                      </h2>
                      <p style={{ color: 'var(--color-text-muted)' }}>Summary, case strength, and recommended actions</p>
                    </div>
                  </div>
                  {showAttorneyAnalysis ? (
                    <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  )}
                </button>

                {showAttorneyAnalysis && (
                  <div className="px-6 pb-6 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                    <div className="pt-4 prose max-w-none">
                      {typeof caseData.attorney_analysis === 'string' ? (
                        <p style={{ color: 'var(--color-text-secondary)' }}>{caseData.attorney_analysis}</p>
                      ) : (
                        <div className="space-y-4">
                          {/* The attorney agent writes `executive_summary` /
                              `recommended_actions` / `blocking_issues`. Read
                              those real names first, keeping the legacy `summary`
                              name as a fallback for older rows. */}
                          {(caseData.attorney_analysis.executive_summary || caseData.attorney_analysis.summary) && (
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Summary</h4>
                              <p style={{ color: 'var(--color-text-secondary)' }}>
                                {caseData.attorney_analysis.executive_summary || caseData.attorney_analysis.summary}
                              </p>
                            </div>
                          )}
                          {typeof caseData.attorney_analysis.overall_case_strength !== 'number'
                            && caseData.attorney_analysis.not_computed?.overall_case_strength && (
                            // F2: no deterministic case-strength rubric yet (WP8).
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Case Strength</h4>
                              <p>
                                <span
                                  className="font-mono"
                                  data-testid="case-strength-not-computed"
                                  style={{ color: 'var(--color-text-ghost)' }}
                                  title={caseData.attorney_analysis.not_computed.overall_case_strength.reason}
                                >
                                  {NOT_COMPUTED_TEXT}
                                </span>
                                {caseData.attorney_analysis.ready_for_demand ? ' — ready for demand' : ''}
                              </p>
                            </div>
                          )}
                          {typeof caseData.attorney_analysis.overall_case_strength === 'number' && (
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Case Strength</h4>
                              <p style={{ color: 'var(--color-text-secondary)' }}>
                                {caseData.attorney_analysis.overall_case_strength}/100
                                {caseData.attorney_analysis.ready_for_demand ? ' — ready for demand' : ''}
                              </p>
                            </div>
                          )}
                          {Array.isArray(caseData.attorney_analysis.blocking_issues)
                            && caseData.attorney_analysis.blocking_issues.length > 0 && (
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Blocking Issues</h4>
                              <ul className="list-disc list-inside" style={{ color: 'var(--color-text-secondary)' }}>
                                {caseData.attorney_analysis.blocking_issues.map((b: any, i: number) => (
                                  <li key={i}>{typeof b === 'string' ? b : (b?.issue || b?.description || JSON.stringify(b))}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {Array.isArray(caseData.attorney_analysis.recommended_actions)
                            && caseData.attorney_analysis.recommended_actions.length > 0 && (
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Recommended Actions</h4>
                              <ul className="list-disc list-inside" style={{ color: 'var(--color-text-secondary)' }}>
                                {caseData.attorney_analysis.recommended_actions.map((a: any, i: number) => (
                                  <li key={i}>{typeof a === 'string' ? a : (a?.action || a?.description || JSON.stringify(a))}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Adjuster Analysis Section */}
            {caseData.adjuster_analysis && (
              <div
                id="adjuster-analysis"
                ref={adjusterAnalysisRef}
                className="rounded-[13px] border mb-6 overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <button
                  onClick={() => setShowAdjusterAnalysis(!showAdjusterAnalysis)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                    >
                      <Shield className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    </div>
                    <div className="text-left">
                      <h2
                        className="cs-h2"
                      >
                        Adjuster Analysis
                      </h2>
                      <p style={{ color: 'var(--color-text-muted)' }}>What the opposing side may argue</p>
                    </div>
                  </div>
                  {showAdjusterAnalysis ? (
                    <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  )}
                </button>

                {showAdjusterAnalysis && (
                  <div className="px-6 pb-6 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                    <div className="pt-4 prose max-w-none">
                      {typeof caseData.adjuster_analysis === 'string' ? (
                        <p style={{ color: 'var(--color-text-secondary)' }}>{caseData.adjuster_analysis}</p>
                      ) : (
                        <div className="space-y-4">
                          {/* `carrier_perspective`, `potential_challenges` and
                              `counter_arguments` are NOT fields any producer
                              emits — AdjusterAnalysisResult has `position`,
                              `reduction_arguments[]` and `executive_summary`.
                              So this card rendered nothing even when the
                              adjuster agent had run. Render the adjuster's own
                              COMPUTED position rather than inventing narrative;
                              the legacy names stay as fallbacks. */}
                          {(() => {
                            const adj = caseData.adjuster_analysis;
                            const pos = adj.position || {};
                            const exec = adj.executive_summary || {};
                            // WP14: fields the analyzer never assessed (stored value stays 0), derived by
                            // the pure mirror of the backend rule; honour it BEFORE the `pos ?? exec`
                            // chain so the 0 / exec fallback never shows as a real figure.
                            const adjNc: Record<string, { reason?: string }> = adjusterNotComputed(adj);
                            const supported = adjNc.supported_total ? null : (pos.supported_total ?? exec.supported_total);
                            const questioned = adjNc.questioned_total ? null : (pos.questioned_total ?? exec.questioned_total);
                            const denied = adjNc.denied_total ? null : (pos.denied_total ?? exec.denied_total);
                            const hasPosition = [supported, questioned, denied]
                              .some((v) => typeof v === 'number') || Object.keys(adjNc).length > 0;
                            if (!hasPosition) return null;
                            return (
                              <div>
                                <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                                  Carrier Position
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  {[
                                    { label: 'Supported', value: supported, key: 'supported_total' },
                                    { label: 'Questioned', value: questioned, key: 'questioned_total' },
                                    { label: 'Denied', value: denied, key: 'denied_total' },
                                  ].map((cell) => (
                                    <div
                                      key={cell.label}
                                      className="rounded-lg p-4"
                                      style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                                    >
                                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{cell.label}</p>
                                      <FinancialValue value={cell.value} size="lg" notRunLabel={NOT_COMPUTED_TEXT} zeroLabel={ZERO_TEXT} reason={adjNc[cell.key]?.reason} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                          {Array.isArray(caseData.adjuster_analysis.reduction_arguments)
                            && caseData.adjuster_analysis.reduction_arguments.length > 0 && (
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                                Anticipated Carrier Arguments
                              </h4>
                              <ul className="space-y-2 mt-2">
                                {caseData.adjuster_analysis.reduction_arguments.map((r: any, i: number) => (
                                  <li key={i} style={{ color: 'var(--color-text-secondary)' }}>
                                    <span>{r?.description || r?.argument_type || 'Reduction argument'}</span>
                                    {typeof r?.dollar_impact === 'number' && r.dollar_impact > 0 && (
                                      <span className="ml-2"><FinancialValue value={r.dollar_impact} size="sm" /></span>
                                    )}
                                    {/* What would refute it — the actionable half for the attorney. */}
                                    {r?.counter_evidence_needed && (
                                      <span className="block text-sm" style={{ color: 'var(--color-text-muted)' }}>
                                        Rebut with: {r.counter_evidence_needed}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {caseData.adjuster_analysis.carrier_perspective && (
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Carrier Perspective</h4>
                              <p style={{ color: 'var(--color-text-secondary)' }}>{caseData.adjuster_analysis.carrier_perspective}</p>
                            </div>
                          )}
                          {caseData.adjuster_analysis.potential_challenges && (
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Potential Challenges</h4>
                              <ul className="list-disc list-inside" style={{ color: 'var(--color-text-secondary)' }}>
                                {caseData.adjuster_analysis.potential_challenges.map((c: string, i: number) => (
                                  <li key={i}>{c}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {caseData.adjuster_analysis.counter_arguments && (
                            <div>
                              <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Counter Arguments</h4>
                              <ul className="list-disc list-inside" style={{ color: 'var(--color-text-secondary)' }}>
                                {caseData.adjuster_analysis.counter_arguments.map((c: string, i: number) => (
                                  <li key={i}>{c}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Monte Carlo Results */}
            {caseData.monte_carlo_results && (
              <div
                id="monte-carlo"
                ref={monteCarloRef}
                className="rounded-[13px] border mb-6 p-6"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                  >
                    <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                  </div>
                  <h2
                    className="cs-h2"
                  >
                    Monte Carlo Analysis
                  </h2>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div
                    className="rounded-lg p-4 text-center"
                    style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                  >
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Conservative (P10)</p>
                    <FinancialValue value={caseData.monte_carlo_results.p10} size="xl" />
                  </div>
                  <div
                    className="rounded-lg p-4 text-center"
                    style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                  >
                    <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Median (P50)</p>
                    <FinancialValue value={caseData.monte_carlo_results.p50} size="xl" />
                  </div>
                  <div
                    className="rounded-lg p-4 text-center"
                    style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                  >
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Aggressive (P90)</p>
                    <FinancialValue value={caseData.monte_carlo_results.p90} size="xl" />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Methodology & Defense Stage Content — the attorney elects the sworn BI-loss
            method here; the election feeds the sworn PoL / demand / court bundle. */}
        {activeLifecycleTab === 'methodology' && (
          <AttorneyMethodologyDefenseView />
        )}

        {/* Deliverables Stage Content */}
        {activeLifecycleTab === 'deliverables' && (
          <>
            {/* Pro Forma Summary. Moved here from Analysis (2026-08-20): the demand
                breakdown is a deliverable output, and LEGACY_HASH_MAP['pro-forma']
                now routes to this tab. The backend returns `{}` when the future-
                closure engine has not run — and `{}` is truthy, so require at least
                one real figure (hasProFormaFigures) before claiming a pro forma. */}
            {hasProFormaFigures && (
              <div
                id="pro-forma"
                className="rounded-[13px] border mb-6 overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <button
                  onClick={() => setShowProForma(!showProForma)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                    >
                      <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    </div>
                    <h2
                      className="cs-h2"
                    >
                      Pro Forma Summary
                    </h2>
                  </div>
                  {showProForma ? (
                    <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  )}
                </button>

                {showProForma && (
                  <div className="px-6 pb-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div
                        className="rounded-lg p-4"
                        style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                      >
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Lost Profits</p>
                        <FinancialValue value={caseData.pro_forma_summary.total_lost_profits} size="lg" />
                      </div>
                      <div
                        className="rounded-lg p-4"
                        style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                      >
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Continued Expenses</p>
                        <FinancialValue value={caseData.pro_forma_summary.total_continued_expenses} size="lg" />
                      </div>
                      <div
                        className="rounded-lg p-4"
                        style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                      >
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>One-Time Costs</p>
                        <FinancialValue value={caseData.pro_forma_summary.one_time_costs} size="lg" />
                      </div>
                      <div
                        className="rounded-lg p-4"
                        style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                      >
                        <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Total Demand</p>
                        <FinancialValue value={caseData.pro_forma_summary.total_demand} size="lg" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Generator Sections */}
            <section id="demand-letter" className="mb-6">
              <h2
                className="cs-h2 mb-1"
              >
                Demand Letter Generator
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Generate professional demand letters with state-specific statutory references
              </p>
              <div
                className="rounded-[13px] border p-6"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                  >
                    <FileText className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Create a Demand Letter</h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      Auto-populated with case data. Supports initial demand, follow-up, bad faith notice, and final demand formats.
                    </p>
                  </div>
                  <Link
                    to={`/attorney/case/${caseId}/demand-letter`}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  >
                    <FileText className="w-4 h-4" />
                    Open Generator
                  </Link>
                </div>
              </div>
            </section>

            <section id="proof-of-loss" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <h2
                className="cs-h2 mb-1"
              >
                Sworn Proof of Loss Generator
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Generate a sworn statement in proof of loss — sworn figures sourced from the forensic engine
              </p>
              <div
                className="rounded-[13px] border p-6"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                  >
                    <ScrollText className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Create a Proof of Loss</h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      A common condition precedent to suit. Supports sworn, partial, amended, and supplemental formats.
                    </p>
                  </div>
                  <Link
                    to={`/attorney/case/${caseId}/proof-of-loss`}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  >
                    <ScrollText className="w-4 h-4" />
                    Open Generator
                  </Link>
                </div>
              </div>
            </section>

            <section id="euo-prep-memo" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <h2
                className="cs-h2 mb-1"
              >
                EUO Preparation Memo
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Prepare the insured for the carrier's Examination Under Oath — privileged attorney work product
              </p>
              <div
                className="rounded-[13px] border p-6"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                  >
                    <Compass className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Create an EUO Prep Memo</h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      Rights & obligations, anticipated topics, engine-sourced figures to substantiate, do's & don'ts.
                    </p>
                  </div>
                  <Link
                    to={`/attorney/case/${caseId}/euo-prep-memo`}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  >
                    <Compass className="w-4 h-4" />
                    Open Generator
                  </Link>
                </div>
              </div>
            </section>

            <section id="ror-response" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <h2
                className="cs-h2 mb-1"
              >
                Response to Reservation of Rights
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                The insured's outbound reply to the carrier's RoR
              </p>
              <div
                className="rounded-[13px] border p-6"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                  >
                    <ShieldCheck className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Create a RoR Response</h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      Acknowledges the reservation, demands the exact policy language, rebuts the reserved grounds.
                    </p>
                  </div>
                  <Link
                    to={`/attorney/case/${caseId}/ror-response`}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Open Generator
                  </Link>
                </div>
              </div>
            </section>

            <section id="ror-analysis" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <h2
                className="cs-h2 mb-1"
              >
                RoR Coverage-Analysis Memo
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Internal analysis of a received Reservation of Rights
              </p>
              <div
                className="rounded-[13px] border p-6"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                  >
                    <FileSearch className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Create a Coverage-Analysis Memo</h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      Attorney work product. Per reserved ground: likely policy basis, the insured's position, rebutting evidence.
                    </p>
                  </div>
                  <Link
                    to={`/attorney/case/${caseId}/ror-analysis`}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  >
                    <FileSearch className="w-4 h-4" />
                    Open Generator
                  </Link>
                </div>
              </div>
            </section>
          </>
        )}

        {/* Settlement Stage Content */}
        {activeLifecycleTab === 'settlement' && (
          <>
            {!hasFairSettlementFigures && (
              <div
                className="rounded-[13px] border p-8 mb-6"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <EmptyState
                  icon={<Target className="w-12 h-12" style={{ color: 'var(--color-text-dim)' }} />}
                  title="Settlement analysis not available yet"
                  description="Fair settlement range and negotiation guidance will appear here once settlement analysis has been run for this case."
                />
              </div>
            )}

            {/* Fair Settlement Section */}
            {hasFairSettlementFigures && (
              <div
                id="fair-settlement"
                ref={fairSettlementRef}
                className="rounded-[13px] border mb-6 overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <button
                  onClick={() => setShowFairSettlement(!showFairSettlement)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-needs-human-tint)' }}
                    >
                      <Target className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    </div>
                    <div className="text-left">
                      <h2
                        className="cs-h2"
                      >
                        Settlement Valuation
                      </h2>
                      <p style={{ color: 'var(--color-text-muted)' }}>Plaintiff supported position; statistical cross-check below (not a recommendation)</p>
                    </div>
                  </div>
                  {showFairSettlement ? (
                    <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  )}
                </button>

                {showFairSettlement && (
                  <div className="px-6 pb-6 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                    <div className="pt-4 space-y-6">
                      {/* Headline: the engine-attested plaintiff position (deterministic —
                          moves only when an engine input changes, never per-rescore). */}
                      <div data-testid="settlement-headline">
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Supported net claim (plaintiff position)</p>
                        <FinancialValue value={caseData.binding_figures?.net_supported_claim} size="xl" />
                        {caseData.binding_figures?.computed_at && (
                          <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
                            engine run {String(caseData.binding_figures.computed_at).slice(0, 10)}
                          </p>
                        )}
                      </div>

                      {/* Demoted, labeled statistical cross-check — NEVER a recommendation. */}
                      {caseData.fair_settlement.cross_check && (
                        <div
                          data-testid="settlement-cross-check"
                          className="rounded-lg p-4"
                          style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                        >
                          <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
                            {caseData.fair_settlement.cross_check_label}
                          </p>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center">
                              <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>P25</p>
                              <FinancialValue value={caseData.fair_settlement.cross_check.p25} size="lg" className="text-[var(--color-text-muted)]" />
                            </div>
                            <div className="text-center">
                              <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>P50</p>
                              <FinancialValue value={caseData.fair_settlement.cross_check.p50} size="lg" className="text-[var(--color-text-muted)]" />
                            </div>
                            <div className="text-center">
                              <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>P75</p>
                              <FinancialValue value={caseData.fair_settlement.cross_check.p75} size="lg" className="text-[var(--color-text-muted)]" />
                            </div>
                          </div>
                          {caseData.fair_settlement.cross_check.run_id && (
                            <p className="text-xs mt-2" style={{ color: 'var(--color-text-dim)' }}>
                              fair_settlement · {caseData.fair_settlement.cross_check.run_id}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Settlement Tools Sections */}
            <section id="sensitivity" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <div id="historical" style={{ position: 'relative', top: '-100px' }} />
              <div id="what-if" style={{ position: 'relative', top: '-100px' }} />
              <div id="strategy-builder" style={{ position: 'relative', top: '-100px' }} />
              <div id="litigation-econ" style={{ position: 'relative', top: '-100px' }} />
              <h2
                className="cs-h2 mb-1"
              >
                Settlement Intelligence
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Sensitivity analysis, historical benchmarks, what-if scenarios, strategy builder, and litigation economics
              </p>
              <Suspense fallback={<div className="animate-pulse h-64 rounded-xl" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />}>
                <SettlementIntelligenceDashboard
                  initialTab={settlementTab}
                  caseId={caseId}
                  claimValue={caseData.binding_figures?.net_supported_claim ?? caseData.total_demand}
                  embedded={true}
                />
              </Suspense>
            </section>

            {/* Neutral mediation board — deep link to the standalone shared surface
                (not embedded here; it must not read as plaintiff tooling). */}
            {caseId && (
              <section className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
                <div
                  className="flex items-center justify-between gap-3 flex-wrap"
                  style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '12px 16px', background: 'var(--color-bg-surface)' }}
                >
                  <div>
                    <div className="cs-body" style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>Neutral mediation board</div>
                    <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      The neutral best estimate on a shared, non-party surface — for a mediation session.
                    </div>
                  </div>
                  <Link
                    to={`/mediation/${caseId}`} target="_blank" rel="noopener"
                    className="text-sm" style={{ whiteSpace: 'nowrap', color: 'var(--color-text-primary)', textDecoration: 'underline' }}
                  >
                    Open mediation board →
                  </Link>
                </div>
              </section>
            )}

            {/* Game Theory Section */}
            <section id="game-theory" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <h2
                className="cs-h2 mb-1"
              >
                Game Theory Analysis
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Nash equilibrium and negotiation strategy modeling
              </p>
              <Suspense fallback={<div className="animate-pulse h-64 rounded-xl" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />}>
                <GameTheoryDashboard initialClaimValue={caseData.binding_figures?.net_supported_claim ?? caseData.total_demand} />
              </Suspense>
            </section>

            {/* Bad Faith Tracker Section */}
            <section id="bad-faith" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <h2
                className="cs-h2 mb-1"
              >
                Bad Faith Tracker
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Track carrier violations, delays, and bad faith indicators
              </p>
              <Suspense fallback={<div className="animate-pulse h-64 rounded-xl" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />}>
                <BadFaithTracker
                  caseId={caseId}
                  initialState={caseData.state ? caseData.state.trim().toUpperCase().slice(0, 2) : undefined}
                  initialClaimAmount={caseData.binding_figures?.net_supported_claim ?? caseData.total_demand ?? 0}
                />
              </Suspense>
            </section>

            {/* Case Law Search Section */}
            <section id="case-law" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <h2
                className="cs-h2 mb-1"
              >
                Case Law Search
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Search comparable cases and precedent analysis
              </p>
              <Suspense fallback={<div className="animate-pulse h-64 rounded-xl" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />}>
                <CaseLawSearch
                  caseId={caseId}
                  initialEventType={caseData.event_type}
                  initialClaimAmount={caseData.binding_figures?.net_supported_claim ?? caseData.total_demand ?? 0}
                />
              </Suspense>
            </section>

            {/* Evidence Checklist Section */}
            <section id="evidence" className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <h2
                className="cs-h2 mb-1"
              >
                Evidence Checklist
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                Track required evidence and documentation status
              </p>
              <Suspense fallback={<div className="animate-pulse h-64 rounded-xl" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />}>
                <EvidenceChecklist caseId={caseId} />
              </Suspense>
            </section>

            {/* Case Intelligence — War-Room brief, §542 prompt-pay, engine audit. Each panel
                fetches its own data and self-handles NOT-RUN / partial payloads. */}
            {caseId && (
              <section id="case-intelligence" className="mt-8 pt-8 border-t mb-8" style={{ borderColor: 'var(--color-border)' }}>
                <h2
                  className="text-lg font-semibold mb-4 font-serif"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Case Intelligence
                </h2>
                <Suspense fallback={<div className="animate-pulse h-64 rounded-xl" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />}>
                  <div className="mb-6"><WarRoomBriefPanel reportId={caseId} /></div>
                  <div className="mb-6"><Texas542Card draftId={caseId} /></div>
                  <div className="mb-6"><EngineAuditPanel reportId={caseId} /></div>
                </Suspense>
              </section>
            )}

            {/* Case Timeline */}
            <section id="timeline" className="mt-8 pt-8 border-t mb-8" style={{ borderColor: 'var(--color-border)' }}>
              <div
                ref={timelineRef}
                className="rounded-[13px] border overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <button
                  onClick={() => setShowTimeline(!showTimeline)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                    >
                      <Clock className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                    <div className="text-left">
                      <h2
                        className="cs-h2"
                      >
                        Case Timeline
                      </h2>
                      <p style={{ color: 'var(--color-text-muted)' }}>History and milestones</p>
                    </div>
                  </div>
                  {showTimeline ? (
                    <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                  )}
                </button>

                {showTimeline && (
                  <div className="px-6 pb-6 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                    <div className="pt-4">
                      <CaseTimeline events={generateTimelineEvents(caseData)} />
                    </div>
                  </div>
                )}
              </div>
            </section>
          </>
        )}

      </div>
    </AttorneyLayout>
  );
}
